import { ActionIcon, Group, Paper, ScrollArea, Table, Text, TextInput, Tooltip } from "@mantine/core";
import { IconArrowsSort, IconDownload, IconSearch, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { useMemo, useState, type ReactNode } from "react";

import { useT } from "../i18n/script";
import { Empty, fmtDate, fmtNum } from "./ui";

export interface Col<T> {
  key: string;
  label: string;
  type?: "text" | "number" | "integer" | "date" | "bool" | "money";
  decimals?: number;
  render?: (row: T) => ReactNode;
  width?: number | string;
  /** tehničke šifre (EPPO, kodovi) ostaju latinicom i u ćiriličkom prikazu */
  latin?: boolean;
}

const NUM = new Set(["number", "integer", "money"]);

// godine i sezone se prikazuju bez razdvajanja hiljada (2026, ne 2.026)
const YEAR_KEY = /(^|_)(sezona|season|godina|year|planting_year|godina_sadnje)$/i;

export function cell(v: unknown, c: { type?: string; decimals?: number; key?: string }): ReactNode {
  if (v === null || v === undefined || v === "") return <Text c="dimmed" span size="sm">—</Text>;
  if (c.type === "bool" || typeof v === "boolean") return v ? "da" : "ne";
  if (c.type === "date") return fmtDate(v);
  if (c.key && YEAR_KEY.test(c.key) && typeof v === "number") return String(v);
  if (c.type && NUM.has(c.type)) return fmtNum(v, c.decimals ?? (c.type === "integer" ? 0 : 2));
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** Tabela sa sortiranjem, pretragom i izvozom u CSV (klijentski, nad prikazanim podacima). */
export function DataTable<T extends Record<string, unknown>>({ rows, columns, onRowClick, search = true, exportName, totals,
  maxHeight, dense }: {
  rows: T[]; columns: Col<T>[]; onRowClick?: (r: T) => void; search?: boolean; exportName?: string;
  totals?: Record<string, unknown> | null; maxHeight?: number; dense?: boolean;
}) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const shown = useMemo(() => {
    let r = rows;
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => columns.some((c) => String(x[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort) {
      r = [...r].sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        if (va === vb) return 0;
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        const na = Number(va), nb = Number(vb);
        return (!Number.isNaN(na) && !Number.isNaN(nb) ? na - nb : String(va).localeCompare(String(vb), "sr")) * sort.dir;
      });
    }
    return r;
  }, [rows, q, sort, columns]);

  const csv = () => {
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [columns.map((c) => esc(c.label)).join(";"),
      ...shown.map((r) => columns.map((c) => esc(c.type && NUM.has(c.type) && r[c.key] != null ? String(r[c.key]).replace(".", ",") : r[c.key])).join(";"))];
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${exportName ?? "tabela"}.csv`;
    a.click();
  };

  return (
    <Paper withBorder>
      {(search || exportName) && (
        <Group justify="space-between" p="xs">
          {search ? <TextInput size="xs" leftSection={<IconSearch size={14} />} placeholder={t("Pretraga...")} value={q}
            onChange={(e) => setQ(e.currentTarget.value)} w={260} /> : <span />}
          <Group gap="xs">
            <Text size="xs" c="dimmed">{shown.length} / {rows.length}</Text>
            {exportName && <Tooltip label={t("Izvoz u CSV")}><ActionIcon variant="subtle" onClick={csv}><IconDownload size={16} /></ActionIcon></Tooltip>}
          </Group>
        </Group>
      )}
      <ScrollArea.Autosize mah={maxHeight ?? 640} type="auto">
        <Table striped highlightOnHover={!!onRowClick} fz="sm" verticalSpacing={dense ? 2 : 6} stickyHeader>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th key={c.key} w={c.width} style={{ cursor: "pointer", whiteSpace: "nowrap", textAlign: c.type && NUM.has(c.type) ? "right" : undefined }}
                  onClick={() => setSort((s) => (s?.key === c.key ? (s.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}>
                  <Group gap={4} wrap="nowrap" justify={c.type && NUM.has(c.type) ? "flex-end" : undefined}>
                    {t(c.label)}
                    {sort?.key === c.key ? (sort.dir === 1 ? <IconSortAscending size={12} /> : <IconSortDescending size={12} />)
                      : <IconArrowsSort size={12} opacity={0.3} />}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {shown.map((r, i) => (
              <Table.Tr key={(r.id as string | number) ?? i} onClick={() => onRowClick?.(r)} style={{ cursor: onRowClick ? "pointer" : undefined }}>
                {columns.map((c) => (
                  <Table.Td key={c.key} translate={c.latin ? "no" : undefined} style={{ textAlign: c.type && NUM.has(c.type) ? "right" : undefined }}>
                    {c.render ? c.render(r) : cell(r[c.key], c)}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
            {totals && shown.length > 0 && (
              <Table.Tr fw={700}>
                {columns.map((c, i) => (
                  <Table.Td key={c.key} style={{ textAlign: c.type && NUM.has(c.type) ? "right" : undefined }}>
                    {c.key in totals ? cell(totals[c.key], c) : i === 0 ? t("Ukupno") : ""}
                  </Table.Td>
                ))}
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        {!shown.length && <Empty />}
      </ScrollArea.Autosize>
    </Paper>
  );
}
